import {
  PinSvg,
  stripWidthAndHeightFromSvg,
} from '@app/components/Molecules/MapView/RiskMapView/LayerTypes'
import type { ContextualLayer } from '@app/domains/layers/types/layers.types'
import { Map as MapboxMap } from 'mapbox-gl'
import { useEffect, useMemo, useRef } from 'react'

/**
 * Single-contextual renderer.
 *
 * Expects a `ContextualLayer` that contains at least:
 *   - tilesets[0].id
 *   - tilesets[0].sourceLayer
 *   - tilesets[0].type  (e.g. 'vector' | 'point' | 'line')
 */
export interface ContextualSingleLayerProps {
  map: MapboxMap
  layer: ContextualLayer | null
  /** Optional stable id used to namespace source/layer ids. Defaults to layer.id */
  layerId?: string
  /** Optional namespace prefix to avoid collisions with other maps */
  namespace?: string
}

export const ContextualSingleLayer = ({
  map,
  layer,
  layerId,
  namespace = 'detail',
}: ContextualSingleLayerProps) => {
  const renderedLayerIdsRef = useRef<Set<string>>(new Set())

  const id = useMemo(() => {
    if (!layer) return null
    return layerId ?? layer.id
  }, [layer, layerId])

  useEffect(() => {
    // Always clear previously-rendered layers for this component
    clearContextualLayers(map, namespace)
    renderedLayerIdsRef.current.clear()

    if (!layer || !id) return

    const addedIds = addContextualToMap(map, layer, id, namespace)
    for (const lid of addedIds) renderedLayerIdsRef.current.add(lid)

    // Copy ref value to local variable for cleanup
    const renderedLayerIds = renderedLayerIdsRef.current

    return () => {
      clearContextualLayers(map, namespace)
      renderedLayerIds.clear()
    }
  }, [map, layer, id, namespace])

  return null
}

const PUBLIC_ASSETS_BASE_URL = import.meta.env.VITE_PUBLIC_ASSETS_BASE_URL

function resolveIconUrl(s3Path: string): string {
  const lower = s3Path.toLocaleLowerCase()
  if (lower.startsWith('s3://')) {
    const iconId = lower.replace('s3://', '')
    if (!iconId.includes('.')) {
      return `${PUBLIC_ASSETS_BASE_URL}/icons/${iconId}.svg`
    }
    return `${PUBLIC_ASSETS_BASE_URL}/icons/${iconId}`
  }
  return s3Path
}

function addContextualToMap(
  map: MapboxMap,
  layer: ContextualLayer,
  id: string,
  namespace: string,
): string[] {
  const tileset = layer.tilesets?.[0]
  if (!tileset) return []

  const sourceId = `${namespace}-contextual-source-${id}`
  const baseLayerId = `${namespace}-contextual-layer-${id}`
  const outlineLayerId = `${baseLayerId}-outline`

  const tileUrl = `mapbox://${tileset.id}`

  // Insert below labels for readability
  const beforeId = map.getStyle().layers?.find((l) => l.type === 'symbol')?.id

  try {
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'vector',
        url: tileUrl,
        promoteId: 'id',
      })
    }

    // --- POINT ---
    if (tileset.type === 'point') {
      // Prefer icon symbol if we have one; otherwise fall back to circles
      const iconSrc = tileset.icon ?? layer.icon
      if (iconSrc) {
        const iconId = String(iconSrc).toLowerCase().replace('s3://', '').replace('.svg', '')
        const imageId = `${namespace}-${iconId}-default`
        const iconUrl = resolveIconUrl(String(iconSrc))

        const addSymbolLayer = () => {
          if (map.getLayer(baseLayerId)) return
          map.addLayer(
            {
              id: baseLayerId,
              type: 'symbol',
              source: sourceId,
              'source-layer': tileset.sourceLayer,
              layout: {
                'icon-image': imageId,
                'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.33, 18, 1.125],
                'icon-allow-overlap': true,
                'icon-anchor': 'bottom',
                visibility: 'visible',
              },
              paint: {
                'icon-opacity': 1,
              },
              minzoom: 1,
            },
            beforeId,
          )
        }

        if (map.hasImage(imageId)) {
          addSymbolLayer()
        } else {
          fetch(iconUrl)
            .then((res) => res.text())
            .then((iconSvg) => {
              const stripped = stripWidthAndHeightFromSvg(iconSvg)
              const pinWithIcon = PinSvg.replace('<!--ICONSVG-->', stripped)
              const svgBitmap = new Image(42, 51)
              svgBitmap.onload = () => {
                if (!map.hasImage(imageId)) {
                  map.addImage(imageId, svgBitmap)
                }
                addSymbolLayer()
              }
              svgBitmap.src =
                'data:image/svg+xml;base64,' + btoa(pinWithIcon.replace('#777777', '#0b2a48'))
            })
            .catch((err) => {
              console.error(`Failed to load icon for contextual ${id}:`, { iconUrl, err })
            })
        }

        return [baseLayerId]
      }

      if (!map.getLayer(baseLayerId)) {
        map.addLayer(
          {
            id: baseLayerId,
            type: 'circle',
            source: sourceId,
            'source-layer': tileset.sourceLayer,
            paint: {
              'circle-color': '#0b2948',
              'circle-opacity': 0.9,
              'circle-radius': 4,
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 1,
            },
          },
          beforeId,
        )
      }

      return [baseLayerId]
    }

    // --- LINE ---
    if (tileset.type === 'line') {
      if (!map.getLayer(baseLayerId)) {
        map.addLayer(
          {
            id: baseLayerId,
            type: 'line',
            source: sourceId,
            'source-layer': tileset.sourceLayer,
            paint: {
              'line-color': '#0b2948',
              'line-opacity': 0.9,
              'line-width': 2,
            },
          },
          beforeId,
        )
      }
      return [baseLayerId]
    }

    // --- VECTOR (default) ---
    // Render polygons with fill + outline. If the dataset isn't polygonal, the fill may simply show nothing.
    if (!map.getLayer(baseLayerId)) {
      map.addLayer(
        {
          id: baseLayerId,
          type: 'fill',
          source: sourceId,
          'source-layer': tileset.sourceLayer,
          paint: {
            'fill-color': '#0b2948',
            'fill-opacity': 0.45,
          },
        },
        beforeId,
      )
    }

    if (!map.getLayer(outlineLayerId)) {
      map.addLayer(
        {
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          'source-layer': tileset.sourceLayer,
          paint: {
            'line-color': '#0b2948',
            'line-opacity': 0.9,
            'line-width': 1.25,
          },
        },
        beforeId,
      )
    }

    return [baseLayerId, outlineLayerId]
  } catch (error) {
    console.error(`Failed to add contextual ${id}:`, error)
    return []
  }
}

function clearContextualLayers(map: MapboxMap, namespace: string) {
  const style = map.getStyle()
  if (!style) return

  const prefix = `${namespace}-contextual-layer-`
  const contextualLayers = style.layers?.filter((l) => l.id.startsWith(prefix)) || []

  contextualLayers.forEach((l) => {
    if (map.getLayer(l.id)) map.removeLayer(l.id)
    const outlineId = `${l.id}-outline`
    if (map.getLayer(outlineId)) map.removeLayer(outlineId)
  })

  const sourcePrefix = `${namespace}-contextual-source-`
  const contextualSources = Object.keys(style.sources || {}).filter((sid) =>
    sid.startsWith(sourcePrefix),
  )

  contextualSources.forEach((sid) => {
    if (map.getSource(sid)) map.removeSource(sid)
  })
}
